import { CreateListViewData, ListView, ListViewService, UpdateListViewData } from "@/services/list-view";
import { enrichViewFromCreate, mergeViewUpdate } from "@/utils/list-view-utils";
import { useUser } from "@/providers/user";
import { useCallback, useEffect, useRef, useState } from "react";

export function useListViews(referenceTypeId?: number | string) {
    const { user } = useUser();
    const [views, setViews] = useState<ListView[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const lastFetchedRef = useRef<number | string | undefined>(undefined);

    const fetchViews = useCallback(
        async (force = false): Promise<ListView[] | void> => {
            if (!referenceTypeId) {
                setLoading(false);
                return;
            }
            if (!force && referenceTypeId === lastFetchedRef.current) return;
            lastFetchedRef.current = referenceTypeId;
            setLoading(true);
            setError(null);
            try {
                const response = await ListViewService.byReferenceType(referenceTypeId);
                setViews(response);
                return response;
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to fetch views");
                console.error("Error fetching views:", err);
            } finally {
                setLoading(false);
            }
        },
        [referenceTypeId]
    );

    const updateView = useCallback(async (id: number, data: UpdateListViewData): Promise<ListView> => {
        try {
            const updatedView = await ListViewService.partialUpdate(id, data);
            // Preserve can_user_edit and created_by as the update API returns limited fields
            const mergeWithExisting = (prev: ListView[]) =>
                prev.map(view => (view.id === id ? mergeViewUpdate(view, updatedView) : view));
            setViews(mergeWithExisting);
            const existing = views.find(v => v.id === id);
            const merged = existing ? mergeViewUpdate(existing, updatedView) : (updatedView as ListView);
            return merged;
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to update view");
            throw err;
        }
    }, [views]);

    const pinView = useCallback(
        async (id: number, force = false) => {
            try {
                await ListViewService.pin(id);
                // Refresh views to get updated pin status
                await fetchViews(force);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to pin view");
                throw err;
            }
        },
        [fetchViews]
    );

    const unpinView = useCallback(
        async (id: number, force = false) => {
            try {
                await ListViewService.unpin(id);
                // Refresh views to get updated pin status
                await fetchViews(force);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to unpin view");
                throw err;
            }
        },
        [fetchViews]
    );

    const saveView = useCallback(async (id: number, data: UpdateListViewData) => {
        try {
            // Create a CreateListViewData object from UpdateListViewData
            const createData: CreateListViewData = {
                name: data.name || "",
                filters: data.filters || {},
                columns: data.columns || [],
                order_by: data.order_by || { column: "created_at", direction: "desc" },
                reference_type: 0 // This will need to be provided somehow, or we should remove this function
            };
            const updatedView = await ListViewService.saveAs(id, createData);
            setViews(prev => [...prev, updatedView]);
            return updatedView;
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to save view");
            throw err;
        }
    }, []);

    const saveAsView = useCallback(async (data: CreateListViewData) => {
        try {
            const newView = await ListViewService.create(data);
            const enrichedView = enrichViewFromCreate(newView, user) as ListView;
            setViews(prev => [...prev, enrichedView]);
            return enrichedView;
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to save view as");
            throw err;
        }
    }, [user?.id, user?.first_name, user?.last_name]);

    const deleteView = useCallback(async (id: number) => {
        try {
            const response = await ListViewService.delete(id);
            // Vérifier que la suppression s'est bien passée (statut 2xx)
            if (response.ok) {
                setViews(prev => prev.filter(view => view.id !== id));
            } else {
                throw new Error(`Delete failed with status ${response.status}`);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to delete view");
            throw err;
        }
    }, []);

    useEffect(() => {
        fetchViews();
    }, [fetchViews]);

    return {
        views,
        loading,
        error,
        fetchViews,
        updateView,
        pinView,
        unpinView,
        saveAsView,
        saveView,
        deleteView
    };
}
